import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Cpu, ChevronDown, ChevronRight, RefreshCw, CheckCircle2, XCircle, History } from 'lucide-react';

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

type EdrVendor = 'sentinelone' | 'crowdstrike' | 'bitdefender' | 'acronis' | 'sangfor_xdr';

const VENDOR_LABELS: Record<EdrVendor, string> = {
  sentinelone: 'SentinelOne',
  crowdstrike: 'CrowdStrike Falcon',
  bitdefender: 'Bitdefender GravityZone',
  acronis: 'Acronis Cyber Protect',
  sangfor_xdr: 'Sangfor Athena XDR',
};

// CrowdStrike e Acronis usano OAuth2 client-credentials (client id/secret); gli altri una API key
// statica — stesso schema di credenziali di backend/src/services/edr/*.ts.
const OAUTH_VENDORS: EdrVendor[] = ['crowdstrike', 'acronis'];

// Sangfor Athena XDR è l'unico vendor oggi usato anche come console SOC multi-tenant a sé (un
// partner organizza i propri clienti in "gruppi" dentro un'unica istanza Athena): solo per questo
// vendor ha senso restringere la sincronizzazione a un gruppo, invece che all'intera piattaforma.
// SentinelOne: una console MSP raccoglie più clienti finali in un unico account, organizzati per
// Site e Group (visto in produzione: "Default site" con gruppi CLIENTS_<cliente>) — senza scoping
// gli endpoint di tutti i clienti finirebbero nel tenant.
const GROUP_SCOPED_VENDORS: EdrVendor[] = ['sangfor_xdr', 'sentinelone'];

interface EdrIntegration {
  tenant_id: number;
  vendor: EdrVendor;
  base_url: string;
  client_id: string | null;
  group_id: string | null;
  subgroup_id: string | null;
  enabled: boolean;
  last_sync_at: string | null;
  last_sync_status: 'ok' | 'error' | null;
  last_error: string | null;
  has_client_secret: boolean;
  has_api_token: boolean;
}

function VendorRow({ tenantId, vendor, integration, onSaved }: {
  tenantId: number; vendor: EdrVendor; integration: EdrIntegration | undefined; onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [baseUrl, setBaseUrl] = useState(integration?.base_url || '');
  const [clientId, setClientId] = useState(integration?.client_id || '');
  const [clientSecret, setClientSecret] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [groupId, setGroupId] = useState(integration?.group_id || '');
  const [subgroupId, setSubgroupId] = useState(integration?.subgroup_id || '');
  const [enabled, setEnabled] = useState(integration?.enabled || false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string; detail?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isOAuth = OAUTH_VENDORS.includes(vendor);
  const isGroupScoped = GROUP_SCOPED_VENDORS.includes(vendor);
  const [backfillSince, setBackfillSince] = useState(isoDate(new Date(Date.now() - 30 * 86400000)));
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState<{ ok: boolean; message: string } | null>(null);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      await axios.put('/api/edr-integration', {
        tenantId, vendor, baseUrl, clientId: isOAuth ? clientId : undefined,
        clientSecret: isOAuth ? (clientSecret || undefined) : undefined,
        apiToken: !isOAuth ? (apiToken || undefined) : undefined,
        groupId: isGroupScoped ? (groupId.trim() || undefined) : undefined,
        subgroupId: isGroupScoped ? (subgroupId.trim() || undefined) : undefined,
        enabled,
      });
      setClientSecret(''); setApiToken('');
      onSaved();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error while saving the integration');
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`/api/edr-integration/${tenantId}/${vendor}/test`);
      const { endpointCount, scope, scopes, incidents } = res.data.data;
      // Sangfor Athena XDR: il test prova anche l'endpoint incident e riporta lo schema del primo
      // incident reale (la doc ufficiale non lo pubblica) — è il modo per verificare la mappatura.
      // Per gli asset mostra il totale PRIMA del filtro di scope e i branch visibili, così uno
      // scoping sbagliato non si confonde con "nessun asset".
      let message = `Connected — ${endpointCount} endpoint(s) visible`;
      const details: string[] = [];
      if (scopes) {
        if (scopes.error) message += ` (asset scopes: ${scopes.error})`;
        else {
          message += ` (${scopes.total} sampled${scopes.totalInAthena !== undefined ? ` of ${scopes.totalInAthena} in Athena` : ''}`
            + `${scope ? `, scope filter "${scope}"${scopes.resolvedBranchId !== undefined ? ` → branchId ${scopes.resolvedBranchId}` : ' — NOT resolved to a branch id: full-inventory scan, client-side filter only'}` : ', no scope filter — every Athena customer would be imported'}).`;
          if (scopes.allBranches?.length) message += ` Athena branches: ${scopes.allBranches.map((b: { id: number; name: string }) => `${b.name} [${b.id}]`).join(', ')}.`;
          else if (scopes.branchListError) message += ` Branch list: ${scopes.branchListError}.`;
          if (scopes.branches?.length) message += ` In sample: ${scopes.branches.map((b: { label: string; count: number }) => `${b.label} (${b.count})`).join(', ')}.`;
          if (scopes.sample) details.push(`first asset (raw):\n${JSON.stringify(scopes.sample, null, 2)}`);
          if (scopes.preview) details.push(`assets/list raw response:\n${scopes.preview}`);
        }
      } else {
        message += '.';
      }
      // Incident e alert: conteggio, campi del primo elemento e — se la lista è vuota — la
      // risposta grezza direttamente nel messaggio (è l'unico modo per capire se "0" è vero).
      const describeProbe = (label: string, p: any) => {
        if (!p) return;
        if (p.error) { message += ` ${label}: ${p.error}.`; return; }
        message += ` ${label}: ${p.count}${p.sampleKeys?.length ? ` — fields: ${p.sampleKeys.join(', ')}` : ''}${p.dataKeys?.length ? ` (response data keys: ${p.dataKeys.join(', ')})` : ''}${p.count === 0 && p.preview ? ` — raw: ${p.preview}` : ''}.`;
        if (p.sample) details.push(`first ${label.toLowerCase()} (raw):\n${JSON.stringify(p.sample, null, 2)}`);
      };
      if (incidents) {
        describeProbe(`Incidents in the last ${incidents.days ?? 30} days`, incidents);
        describeProbe(`Alerts in the last ${incidents.days ?? 30} days`, incidents.alerts);
      }
      setTestResult({ ok: true, message, detail: details.length ? details.join('\n\n') : undefined });
    } catch (err: any) {
      setTestResult({ ok: false, message: err.response?.data?.error || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  }

  async function handleBackfill() {
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const res = await axios.post(`/api/edr-integration/${tenantId}/${vendor}/backfill`, { sinceDate: backfillSince });
      setBackfillResult({ ok: true, message: `Imported ${res.data.data.endpoints} endpoint(s) and ${res.data.data.alerts} alert(s) since ${backfillSince}.` });
      onSaved();
    } catch (err: any) {
      setBackfillResult({ ok: false, message: err.response?.data?.error || 'Backfill failed' });
    } finally {
      setBackfilling(false);
    }
  }

  return (
    <div className="border border-gray-100 rounded-lg">
      <button type="button" onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 rounded-lg">
        <span className="inline-flex items-center gap-2 font-medium text-gray-800">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />} {VENDOR_LABELS[vendor]}
        </span>
        {integration ? (
          <span className={`badge ${integration.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
            {integration.enabled ? 'Enabled' : 'Configured, disabled'}
          </span>
        ) : (
          <span className="text-xs text-gray-400">Not configured</span>
        )}
      </button>
      {open && (
        <div className="px-3 pb-3">
          {error && <div className="mb-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}
          <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Base URL</label>
              <input value={baseUrl} onChange={e => setBaseUrl(e.target.value)} required
                className="input-field font-mono text-xs" placeholder="https://..." />
            </div>
            {isOAuth ? (
              <>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Client ID</label>
                  <input value={clientId} onChange={e => setClientId(e.target.value)} required className="input-field font-mono text-xs" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Client secret {integration?.has_client_secret && <span className="text-gray-400">(leave blank to keep)</span>}
                  </label>
                  <input type="password" value={clientSecret} onChange={e => setClientSecret(e.target.value)}
                    className="input-field font-mono text-xs" required={!integration?.has_client_secret}
                    placeholder={integration?.has_client_secret ? '••••••••' : ''} />
                </div>
              </>
            ) : (
              <div className={isGroupScoped ? '' : 'md:col-span-2'}>
                <label className="block text-xs text-gray-500 mb-1">
                  {vendor === 'sangfor_xdr' ? 'Connection Code' : 'API token'}
                  {integration?.has_api_token && <span className="text-gray-400"> (leave blank to keep)</span>}
                </label>
                {vendor === 'sangfor_xdr' && (
                  <p className="text-[11px] text-gray-400 mb-1">
                    From the Athena console: Configuration → System → Openness → Connection Keys.
                  </p>
                )}
                <input type="password" value={apiToken} onChange={e => setApiToken(e.target.value)}
                  className="input-field font-mono text-xs" required={!integration?.has_api_token}
                  placeholder={integration?.has_api_token ? '••••••••' : ''} />
              </div>
            )}
            {isGroupScoped && vendor === 'sentinelone' && (
              <>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Group name(s) or ID(s) — this customer's groups</label>
                  <input value={subgroupId} onChange={e => setSubgroupId(e.target.value)}
                    className="input-field font-mono text-xs" placeholder="e.g. CLIENTS_ACME,SERVERS_ACME" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Site name or ID (optional)</label>
                  <input value={groupId} onChange={e => setGroupId(e.target.value)}
                    className="input-field font-mono text-xs" placeholder="e.g. Default site" />
                </div>
                <div className="md:col-span-3 -mt-1">
                  <p className="text-xs text-amber-600">
                    {subgroupId.trim() || groupId.trim()
                      ? `Only agents and threats in ${subgroupId.trim() ? `group(s) "${subgroupId.trim()}"` : ''}${subgroupId.trim() && groupId.trim() ? ' of ' : ''}${groupId.trim() ? `site "${groupId.trim()}"` : ''} are kept for this tenant.`
                      : 'No scoping: EVERY agent visible to this API token is imported into this tenant. On an MSP console with several customers, set the group(s) of this customer exactly as named in the SentinelOne console (comma-separated).'}
                  </p>
                </div>
              </>
            )}
            {isGroupScoped && vendor !== 'sentinelone' && (
              <>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Branch ID or name (customer, in Athena)</label>
                  <input value={subgroupId} onChange={e => setSubgroupId(e.target.value)}
                    className="input-field font-mono text-xs" placeholder="e.g. 57 or APV Investimenti" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Group ID (optional)</label>
                  <input value={groupId} onChange={e => setGroupId(e.target.value)}
                    className="input-field font-mono text-xs" placeholder="usually not required" />
                </div>
                <div className="md:col-span-3 -mt-1">
                  <p className="text-xs text-amber-600">
                    {subgroupId.trim()
                      ? `Only endpoints/detections whose branch matches "${subgroupId.trim()}" will be kept for this tenant (filtered after Athena returns them — the API does not yet support server-side filtering by branch for us).`
                      : "Athena's assets/list API always returns the whole platform for now (server-side branch filtering isn't confirmed working yet) — set the branch ID or name exactly as shown in Athena so only this customer's data is kept for this tenant."}
                  </p>
                </div>
              </>
            )}
            <div className="md:col-span-3 flex items-center justify-between">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
                Enabled (syncs every 15 minutes)
              </label>
              <div className="flex items-center gap-2">
                {integration && (
                  <button type="button" onClick={handleTest} disabled={testing} className="btn-secondary flex items-center gap-1 disabled:opacity-50">
                    <RefreshCw size={13} className={testing ? 'animate-spin' : ''} /> Test connection
                  </button>
                )}
                <button type="submit" disabled={saving} className="btn-primary disabled:opacity-50">
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </form>
          {testResult && (
            <div className={`mt-2 text-sm rounded-lg px-3 py-2 inline-flex items-center gap-1.5 ${testResult.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
              {testResult.ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />} {testResult.message}
            </div>
          )}
          {testResult?.detail && (
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer text-gray-500">Raw details (first incident / raw responses)</summary>
              <pre className="mt-1 max-h-72 overflow-auto rounded-lg bg-gray-900 text-gray-100 p-3 font-mono text-[11px]">{testResult.detail}</pre>
            </details>
          )}
          {integration?.last_sync_at && (
            <p className="mt-2 text-xs text-gray-400">
              Last sync: {new Date(integration.last_sync_at).toLocaleString('en-GB')} —{' '}
              {integration.last_sync_status === 'ok'
                ? <span className="text-emerald-600">ok</span>
                : <span className="text-red-600" title={integration.last_error || ''}>error{integration.last_error ? `: ${integration.last_error}` : ''}</span>}
            </p>
          )}
          {integration && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-xs font-medium text-gray-600 mb-1.5 inline-flex items-center gap-1.5">
                <History size={12} /> Backfill historical logs
              </p>
              <p className="text-xs text-gray-400 mb-2">
                One-off import of endpoints and detections from a past date, in addition to the regular
                15-minute sync. Does not affect the incremental sync schedule. The vendor API returns a
                single page of results, so on a wide date range only the most recent detections within
                that page will be imported.
              </p>
              <div className="flex items-center gap-2">
                <input type="date" value={backfillSince} onChange={e => setBackfillSince(e.target.value)}
                  className="input-field py-1 px-2 text-xs w-auto" max={isoDate(new Date())} />
                <button type="button" onClick={handleBackfill} disabled={backfilling}
                  className="btn-secondary flex items-center gap-1 disabled:opacity-50">
                  <RefreshCw size={13} className={backfilling ? 'animate-spin' : ''} /> {backfilling ? 'Importing…' : 'Backfill'}
                </button>
              </div>
              {backfillResult && (
                <div className={`mt-2 text-sm rounded-lg px-3 py-2 inline-flex items-center gap-1.5 ${backfillResult.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                  {backfillResult.ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />} {backfillResult.message}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Configurazione EDR multi-vendor per il tenant selezionato: SentinelOne, CrowdStrike, Bitdefender,
// Acronis, Sangfor EPP possono coesistere (es. durante una migrazione tra prodotti). Ogni vendor è
// sincronizzato da backend/src/jobs/edrSync.ts tramite l'adapter corrispondente in services/edr/.
export default function EdrIntegrationPanel({ tenantId }: { tenantId: number | null }) {
  const { user } = useAuth();
  const platformAdmin = user?.scopeLevel === 'platform' && user?.role === 'admin';
  const [integrations, setIntegrations] = useState<EdrIntegration[]>([]);
  const [loading, setLoading] = useState(true);

  function load() {
    if (!tenantId) return;
    setLoading(true);
    axios.get('/api/edr-integration', { params: { tenantId } })
      .then(res => setIntegrations(res.data.data))
      .finally(() => setLoading(false));
  }

  useEffect(load, [tenantId]);

  if (!tenantId) return null;

  return (
    <div className="card p-4 mb-4">
      <h2 className="font-medium text-sm text-gray-900 mb-1 inline-flex items-center gap-2">
        <Cpu size={15} /> EDR integrations
      </h2>
      <p className="text-xs text-gray-400 mb-3">
        Import endpoints and detections from the customer's EDR: endpoints are merged into the
        Asset Inventory (with IP/MAC when available), detections open cases/alerts like any other
        detection rule.
      </p>
      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : (
        <div className="space-y-2">
          {(['sentinelone', 'crowdstrike', 'bitdefender', 'acronis', 'sangfor_xdr'] as EdrVendor[])
            // Sangfor Athena XDR sospesa (29/10/2026): visibile solo ai platform admin; per Sangfor
            // si usa l'ingestion diretta di Athena EPP (pannello "Sangfor Athena EPP consoles").
            .filter(vendor => vendor !== 'sangfor_xdr' || platformAdmin)
            .map(vendor => (
            <VendorRow key={vendor} tenantId={tenantId} vendor={vendor}
              integration={integrations.find(i => i.vendor === vendor)} onSaved={load} />
          ))}
        </div>
      )}
    </div>
  );
}
